/**
 * 🎨 **그림 초안 만들기(운영자용 AI)** — 무엇을 그릴지 말로 적으면 후보 그림을 받는다.
 *
 * ## 이건 초안이지 정답이 아니다
 * 굿즈 초안·자막 읽기와 같은 계열이다(docs/build/00 S4-1). **바로 게시되지 않는다** —
 * 후보를 보여 주고 운영자가 고른 뒤, [🚀 웹에 올리기]를 눌러야 아이에게 간다.
 *
 * ## 🔴 그림체가 가장 어렵다
 * 《그레그, 경주로 폴짝!》은 **연필 선 + 수채 담채**에 흰 유령 같은 그레그, 노란 병아리다.
 * 그냥 「밀짚모자 그려줘」 하면 3D 렌더나 반짝이는 일러스트가 나와 **그것만 튄다.**
 * 그래서 ① 그림체를 프롬프트에 박고 ② 무엇을 만드는지에 따라 배경·구도를 못 박는다.
 * 그래도 한 번에 안 나온다 — 후보를 여러 장 뽑아 고르는 흐름이 전제다.
 *
 * ## 왜 판정을 여기 두나
 * 프롬프트는 **공급자 중립**이어야 한다(S4-2). 여기서 문자열만 만들고, 실제 호출은
 * `apps/edge/artApi.ts` 가 한다. 나중에 다른 공급자를 붙여도 이 파일은 그대로다.
 */

/** 만들 수 있는 것 — [➕ 직접 만들기]의 종류와 짝을 맞춘다. */
export type ArtKind = "hero" | "hair" | "acc" | "npc" | "decor" | "bg";

export interface ArtKindSpec {
  label: string;
  /** 배경이 지워져야 하나. 아이 위에 얹히는 것·마을에 놓이는 것은 전부 투명이어야 한다. */
  transparent: boolean;
  /** 그림의 모양. 배경만 가로로 길다. */
  size: "1024x1024" | "1536x1024";
  /** 무엇을 어떻게 그릴지 — 종류마다 구도가 다르다. */
  frame: string;
  /** 운영자에게 보여줄 예시(빈 칸에 뭘 적어야 할지 모르는 게 가장 흔한 벽이다). */
  example: string;
}

export const ART_KINDS: Record<ArtKind, ArtKindSpec> = {
  /**
   * 완성 캐릭터 — 아이가 고르는 주인공. **서 있는 모습 한 장**만 만든다.
   * 걷기 그림(앞·옆·뒤)은 네 칸이 가로로 이어진 시트라 AI 로 일관되게 뽑기 어렵다 —
   * 없으면 서 있는 모습으로 나오는 폴백이 이미 있다.
   */
  hero: {
    label: "히어로 캐릭터",
    transparent: true,
    size: "1024x1024",
    frame:
      "One friendly child character standing and facing the viewer, full body from head to feet, " +
      "centered, filling most of the square. Calm neutral pose, arms relaxed, both feet visible. " +
      "No ground, no shadow, no scenery, no other characters.",
    example: "파란 모자를 쓴 탐험가 아이",
  },
  hair: {
    label: "모자·머리장식",
    transparent: true,
    size: "1024x1024",
    frame:
      "A single hat or head accessory by itself, drawn from the front, centered, filling most of the square. " +
      "No head, no face, no body, no mannequin, no stand. Nothing else in the picture.",
    example: "밀짚모자, 챙이 넓고 노란 지푸라기",
  },
  acc: {
    label: "소품",
    transparent: true,
    size: "1024x1024",
    frame:
      "A single small object a child could hold or wear, by itself, from the front, centered. " +
      "No hands, no person, no table, no shadow on the ground. Nothing else in the picture.",
    example: "나무 지팡이, 끝에 작은 별이 달림",
  },
  npc: {
    label: "마을 친구",
    transparent: true,
    size: "1024x1024",
    frame:
      "One friendly character standing and facing the viewer, full body, feet included, centered. " +
      "Calm neutral pose, arms relaxed. No ground, no shadow, no scenery.",
    example: "아기 여우, 목도리를 두른 작은 여우",
  },
  decor: {
    label: "꾸미기 물건",
    transparent: true,
    size: "1024x1024",
    frame:
      "One object that would sit in a village scene (tree, rock, lantern, signpost), seen from the front, " +
      "standing upright, centered, full object with its base included. No ground plane, no shadow, no scenery.",
    example: "작은 소나무, 굽은 줄기",
  },
  bg: {
    label: "마을 배경",
    transparent: false,
    size: "1536x1024",
    frame:
      "A wide, calm background scene for a children's village, drawn as flat scenery with an empty middle " +
      "area where characters will walk. No characters, no people, no animals, no text.",
    example: "달빛 아래 기와지붕 마을, 멀리 첨성대",
  },
};

/**
 * 🎨 **그림체** — 이 책의 결. 없으면 AI 가 자기 취향대로 그린다.
 *
 * 운영자가 고칠 수 있어야 한다(책이 늘면 결이 달라진다) — 화면이 값을 넘겨주고,
 * 안 넘기면 이 기본값을 쓴다.
 */
export const ART_STYLE_DEFAULT =
  "Korean picture-book illustration: soft pencil linework with light watercolor washes, " +
  "warm muted colors, visible paper grain, gentle and quiet, hand-drawn. " +
  "Not 3D, not glossy, not vector, not neon, no outlines that are too clean.";

/** 한 번에 뽑을 후보 수 — 그림은 비싸다. 셋이면 고를 수 있고, 그 이상은 낭비다. */
export const ART_CANDIDATES_MAX = 3;
/** 운영자가 적는 글의 길이 상한. 길다고 좋아지지 않는다. */
export const ART_WHAT_MAX = 200;
export const ART_STYLE_MAX = 500;
/** 하루 상한 — 그림은 소리보다 훨씬 비싸다. 오작동·비용 폭주 차단. */
export const ART_DAILY_LIMIT = 60;

/** 요청 하나. 공급자에 상관없이 이 모양으로 만든다. */
export interface ArtDraftRequest {
  prompt: string;
  size: ArtKindSpec["size"];
  transparent: boolean;
}

/**
 * 🔴 **프롬프트를 두 토막으로 나눈다** — 운영자가 고칠 수 있는 곳과, 늘 붙는 곳.
 *
 * 통째로 열어 주면 「0~7세 그림책·무서운 것 없음·글자 없음」이나 투명 지시를 **실수로
 * 지울 수 있다.** 그러면 모자에 흰 판이 붙어 오고, 운영자는 왜 그런지 알 길이 없다.
 * 반대로 통째로 잠그면 원하는 그림을 못 만든다 — 그리는 사람은 운영자다.
 *
 * 그래서 **무엇을 어떻게 그릴지**(editable)만 열고, **안전·투명**(fixed)은 늘 뒤에 붙인다.
 * 화면에도 그렇게 보여 준다 — 보이는 것과 보내는 것이 갈리지 않게.
 */
export interface ArtPromptParts {
  /** 운영자가 고치는 부분 — 무엇을·어떤 구도로·어떤 결로. */
  editable: string;
  /** 늘 뒤에 붙는 부분 — 아이 안전과 투명. 화면에는 보이되 못 고친다. */
  fixed: string;
}

/** 운영자가 고칠 수 있는 최대 길이 — 길다고 좋아지지 않는다. */
export const ART_PROMPT_MAX = 1200;

export function artPromptParts(kind: ArtKind, what: string, style?: string): ArtPromptParts {
  const k = ART_KINDS[kind];
  const said = what.trim().slice(0, ART_WHAT_MAX);
  const look = (style?.trim() || ART_STYLE_DEFAULT).slice(0, ART_STYLE_MAX);
  const fixed = [
    "For a picture book for children aged 0-7: gentle, friendly, nothing scary, no weapons, no text or letters anywhere.",
    // 투명이 필요한 것은 **말로도** 못 박는다 — 배경 옵션만 믿으면 흰 판을 그려 넣는 모델이 있다.
    k.transparent ? "The background must be fully transparent — no backdrop, no ground, no shadow." : "",
  ]
    .filter(Boolean)
    .join(" ");
  return { editable: [`Subject: ${said}.`, k.frame, `Style: ${look}`].join(" "), fixed };
}

/**
 * 실제로 보낼 요청.
 *
 * @param custom 운영자가 고친 앞부분. 주면 그걸 쓰고, 안 주면 기본으로 만든다.
 *               **뒷부분(안전·투명)은 어느 쪽이든 늘 붙는다.**
 */
export function artDraftRequest(
  kind: ArtKind,
  what: string,
  style?: string,
  custom?: string,
): ArtDraftRequest {
  const k = ART_KINDS[kind];
  const parts = artPromptParts(kind, what, style);
  const head = (custom?.trim() || parts.editable).slice(0, ART_PROMPT_MAX);
  return { prompt: `${head} ${parts.fixed}`.trim(), size: k.size, transparent: k.transparent };
}

/** 밖에서 온 종류 값이 우리가 아는 것인가(불변식 7). */
export function isArtKind(v: unknown): v is ArtKind {
  return typeof v === "string" && Object.prototype.hasOwnProperty.call(ART_KINDS, v);
}

/**
 * 🖼️ **참고 그림** — 「이 그림처럼 그려 주세요」.
 *
 * 그림체가 한 번에 안 맞는 게 가장 큰 벽이었다. 말로 아무리 적어도 모델은 자기 취향으로
 * 그린다. 이미 있는 그림을 같이 보내면 결이 훨씬 붙는다 — 목록에 나란히 놓았을 때
 * 그것만 튀지 않는다.
 */
export const ART_REFS_MAX = 4;

/**
 * 참고 그림을 보낼 때 프롬프트 끝에 붙이는 말.
 *
 * 🔴 **「따라 그리라」가 아니라 「결을 맞추라」** 고 해야 한다. 안 그러면 참고 그림을
 * 그대로 베껴 와서, 새 모자를 만들라고 했는데 있던 모자가 다시 나온다.
 */
export const ART_REF_HINT =
  "Match the drawing style, line quality, color feel and proportions of the reference images. " +
  "Do not copy their subject — draw what the subject line above asks for.";
